import { type ReactNode } from 'react';
import { Icon, type IconName } from '../Icon.js';

/* Bitácora / acordeón V2 (handoff, tabla "Lo que se repite"):
 * la bitácora de Outreach, la de Mensajes y el acordeón de Task Manager son
 * EL MISMO componente. Entrada colapsada: disco del canal · canal · título ·
 * pill de estado · hora · chevron. El COLOR del disco dice el canal
 * (--evo-ch-*); el RELLENO dice la dirección — relleno tenue = entrante,
 * solo contorno = saliente. La dirección nunca se rotula con texto.
 * Varias entradas pueden estar abiertas a la vez: el estado vive en el
 * caller, por id. */

export type LogChannel =
  | 'email'
  | 'instagram'
  | 'whatsapp'
  | 'sms'
  | 'call'
  | 'system'
  | 'note';

const CHANNEL_VAR: Record<LogChannel, string> = {
  email: 'var(--evo-ch-email)',
  instagram: 'var(--evo-ch-instagram)',
  whatsapp: 'var(--evo-ch-whatsapp)',
  sms: 'var(--evo-ch-sms)',
  call: 'var(--evo-ch-call)',
  system: 'var(--evo-ch-system)',
  note: 'var(--evo-ch-note)',
};

const CHANNEL_ICON: Record<LogChannel, IconName> = {
  email: 'email',
  instagram: 'social-instagram',
  whatsapp: 'social-whatsapp',
  sms: 'messages',
  call: 'phone',
  system: 'bolt',
  note: 'pencil',
};

export interface LogEntryProps {
  channel: LogChannel;
  /** Etiqueta de la columna fija (Email · Instagram · Nota interna · …). */
  channelLabel: string;
  /** Icono a mostrar en el disco; default: el del canal. */
  icon?: IconName;
  /** Color del disco; default: token del canal. Para tareas usar --evo-task-*. */
  color?: string;
  /** entrante = relleno tenue del disco · saliente = solo contorno. */
  direction?: 'in' | 'out';
  title: ReactNode;
  pill?: ReactNode;
  time?: string;
  open: boolean;
  onToggle: () => void;
  /** Cuerpo expandido: KVs, párrafo, acciones. Sin banda — sobre el negro de la página. */
  children?: ReactNode;
}

export function LogEntry({
  channel,
  channelLabel,
  icon,
  color,
  direction = 'out',
  title,
  pill,
  time,
  open,
  onToggle,
  children,
}: LogEntryProps) {
  const c = color ?? CHANNEL_VAR[channel];
  return (
    <div style={{
      margin: '0 var(--evo-log-inset)',
      border: '1px solid var(--evo-stroke)',
      borderRadius: 'var(--evo-r)',
      overflow: 'hidden',
    }}>
      <div
        onClick={onToggle}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-log-row-bg-hover)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-log-row-bg)'; }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--evo-log-icon-gap)',
          minHeight: 'var(--evo-log-row-min-h)',
          boxSizing: 'border-box',
          padding: 'var(--evo-log-row-py) var(--evo-log-row-px)',
          background: 'var(--evo-log-row-bg)',
          cursor: 'pointer',
          transition: 'background 80ms',
        }}
      >
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          width: 'var(--evo-log-icon-sz)',
          height: 'var(--evo-log-icon-sz)',
          flexShrink: 0,
          borderRadius: 'var(--evo-r-pill)',
          border: `1px solid ${c}`,
          background: direction === 'in' ? `color-mix(in srgb, ${c} 14%, transparent)` : 'transparent',
          color: c,
        }}>
          <Icon name={icon ?? CHANNEL_ICON[channel]} size={13} />
        </span>
        <span style={{
          flexShrink: 0,
          width: 'var(--evo-log-label-w)',
          fontSize: 'var(--evo-fs-caption)',
          fontFamily: 'var(--evo-font)',
          color: 'var(--evo-fg-4)',
          whiteSpace: 'nowrap',
        }}>
          {channelLabel}
        </span>
        <span style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--evo-list-title-fs)',
          fontFamily: 'var(--evo-font)',
          color: 'var(--evo-fg-1)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {title}
        </span>
        {pill != null && <span style={{ flexShrink: 0, display: 'inline-flex' }}>{pill}</span>}
        {time != null && (
          <span style={{
            flexShrink: 0,
            fontSize: 'var(--evo-fs-caption)',
            fontFamily: 'var(--evo-font)',
            color: 'var(--evo-fg-3)',
            whiteSpace: 'nowrap',
          }}>
            {time}
          </span>
        )}
        <span style={{
          flexShrink: 0,
          display: 'inline-flex',
          color: 'var(--evo-fg-4)',
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 150ms',
        }}>
          <Icon name="chevron-down" size={12} />
        </span>
      </div>
      {open && children != null && (
        <div style={{ padding: '0 var(--evo-log-row-px) var(--evo-log-body-py)', boxSizing: 'border-box' }}>
          <div style={{
            padding: 'var(--evo-space-4) 0 0',
            borderTop: '1px solid var(--evo-stroke)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--evo-space-4)',
          }}>
            {children}
          </div>
        </div>
      )}
    </div>
  );
}

/* Banda de fecha de la bitácora (Programado · Hoy · Esta semana · Antes).
 * Sin marco: etiqueta suelta alineada al interior de las entradas. */
export function LogGroupHeader({ label, count }: { label: string; count?: number }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: 'var(--evo-space-3) calc(var(--evo-log-inset) + var(--evo-log-row-px)) var(--evo-space-2)',
      fontSize: 'var(--evo-fs-label)',
      lineHeight: 1,
      fontWeight: 'var(--evo-fw-semibold)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: 'var(--evo-fg-3)',
      userSelect: 'none',
      fontFamily: 'var(--evo-font)',
    }}>
      <span style={{ display: 'inline-flex', flexShrink: 0 }}><Icon name="chevron-down" size={10} /></span>
      <span style={{ flex: 1 }}>{label}</span>
      {count != null && <span style={{ fontWeight: 400, textTransform: 'none' }}>({count})</span>}
    </div>
  );
}

/* Fila de KV compacta dentro del cuerpo expandido de una entrada. */
export function LogKV({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-detail-kv-gap)' }}>
      <span style={{
        flexShrink: 0,
        fontSize: 'var(--evo-fs-label)',
        color: 'var(--evo-text-muted)',
        fontFamily: 'var(--evo-font)',
      }}>
        {label}
      </span>
      <span style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 'var(--evo-space-2)',
        textAlign: 'right',
        fontSize: 'var(--evo-list-title-fs)',
        fontFamily: 'var(--evo-font)',
        color: 'var(--evo-text-strong)',
      }}>
        {value}
      </span>
    </div>
  );
}
